import getRecaptchaToken from '../helpers/getRecaptchaToken';
import AppContext from './context/AppContext';
import { Form, Image, Row } from 'react-bootstrap';
import PropTypes from 'prop-types';
import React, { useContext, useState } from 'react';
import '../styles/QuestionsAndAnswersForm.css';
import LoadingFallback from './LoadingFallback';
import { ToastContext } from './context/ToastContext';

const IMAGE_FILE_TYPE_PREFIX = 'image/';

/**
 * A form for submitting questions.
 * @component
 */
const QuestionsAndAnswersForm = ({
    productId,
    hidden = false,
    onQuestionSubmit = () => { }
}) => {
    /** Contexts */
    const { appContext } = useContext(AppContext);

    /** States */
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [title, setTitle] = useState('');
    const [question, setQuestion] = useState('');
    const [attachmentsPreview, setAttachmentsPreview] = useState(null);
    const [imageFiles, setImageFiles] = useState([]);
    const [loading, setLoading] = useState(false);
    const { sendMessage } = useContext(ToastContext);

    /** State Handlers */
    const onChangeName = (e) => setName(e.target.value);
    const onChangeEmail = (e) => setEmail(e.target.value);
    const onChangeQuestion = (e) => setQuestion(e.target.value);
    const onChangeTitle = (e) => setTitle(e.target.value);

    /** Form Event Handlers */
    /**
     * Handle form submission.
     * @param {Object} e The event object.
     */
    const onSubmit = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        await submitQuestion();
    };

    /**
     * Submit a question.
     */
    const submitQuestion = async () => {
        try {
            setLoading(true);
            const url = new URL('/api/questions', process.env.APP_URL);
            const formData = new FormData();
            formData.append('name', name);
            formData.append('email', email);
            formData.append('title', title);
            formData.append('question', question);
            formData.append('productId', productId);
            if (imageFiles.length >= 0) {
                for (let i = 0; i < imageFiles.length; i++) {
                    formData.append('images', imageFiles[i]);
                }
            }
            const response = await fetch(url, {
                method: 'POST',
                body: formData,
                headers: {
                    'x-grecaptcha-token': await getRecaptchaToken(appContext.config.recaptchaSiteKey),
                },
            });
            if (!response.ok)
                throw new Error('API request failed: bad response.');
            const data = await response.json();
            if (!data.question) {
                throw new Error('Did not get the expected response from server.');
            }
            setAttachmentsPreview(null);
            setImageFiles([]);
            // Reset form.
            setName('');
            setEmail('');
            setQuestion('');
            // Call function to execute after submission.
            onQuestionSubmit();
            sendMessage('Question saved', 'success');
        } catch (e) {
            sendMessage('Question not saved', 'danger');
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    /** Image Event Handlers */
    const handleUploadImageChange = (e) => {
        try {
            if (!e.target.files.length)
                throw new Error('No uploaded image available to preview');

            const imgFiles = e.target.files;
            const attachmentsPreview = [];
            for (let i = 0; i < imgFiles.length; i++) {
                if (imgFiles[i].type.startsWith(IMAGE_FILE_TYPE_PREFIX)) {
                    attachmentsPreview.push({
                        isImage: true,
                        base64: URL.createObjectURL(imgFiles[i]),
                    });
                } else {
                    attachmentsPreview.push({
                        isImage: false,
                        fileName: imgFiles[i].name
                    });
                }
                
            }
            setAttachmentsPreview(attachmentsPreview);
            setImageFiles(imgFiles);
        } catch (e) {
            console.error(e);
        }
    };

    /** UI */
    return (
        <>
            {
                loading ? <LoadingFallback /> :
                    <Form
                        className={`mb-5${hidden ? ' d-none' : ''}`}
                        id={'ask-question-form'}
                        onSubmit={onSubmit}
                    >
                        <Form.Group
                            className={'mb-3'}
                            controlId={'ask-question-form--name'}>
                            <Form.Label>Name</Form.Label>
                            <Form.Control
                                value={name}
                                onChange={onChangeName} />
                        </Form.Group>
                        <Form.Group
                            className={'mb-3'}
                            controlId={'ask-question-form--email'}>
                            <Form.Label>Email</Form.Label>
                            <Form.Control
                                value={email}
                                onChange={onChangeEmail} />
                        </Form.Group>
                        <Form.Group
                            className={'mb-3'}>
                            <Form.Label>Title</Form.Label>
                            <Form.Control
                                value={title}
                                onChange={onChangeTitle}
                            />
                        </Form.Group>
                        <Form.Group
                            className={'mb-3'}
                            controlId={'ask-question-form--question'}>
                            <Form.Label>Question</Form.Label>
                            <Form.Control
                                as={'textarea'}
                                rows={3}
                                value={question}
                                onChange={onChangeQuestion}
                            />
                        </Form.Group>
                        <Form.Group
                            className={'mb-3'}
                            controlId={'answerForm.AnswerImages'}>
                            <Form.Label>Upload Attachments (Optional)</Form.Label>
                            {attachmentsPreview && (<Row
                                xs={1}
                                md={3}
                                lg={4}
                            >
                                {
                                    attachmentsPreview.map(({ isImage, base64, fileName }, index) => {
                                        return (
                                            <div
                                                key={index}
                                            >
                                                {isImage ? (
                                                    <Image
                                                        src={base64}
                                                        alt={''}
                                                        width={'100%'}
                                                        rounded
                                                    />
                                                ) : (
                                                    <div>{fileName}</div>
                                                )}
                                            </div>
                                        );
                                    })
                                }
                            </Row>)}
                            <br />
                            <Form.Control
                                type={'file'}
                                multiple
                                onChange={handleUploadImageChange}
                                style={{ marginBottom: '10px' }} />
                        </Form.Group>
                        <button
                            className={'ask-question-form--submit-button'}
                            type={'submit'}>
                            Submit
                        </button>
                    </Form>
            }
        </>
    );
};

/** Prop Types */
QuestionsAndAnswersForm.propTypes = {
    /** The product ID. */
    productId: PropTypes.number.isRequired,
    /** Indicate whether the form should be shown or not. */
    hidden: PropTypes.bool,
    /** Callback function to call after a question is submitted. */
    onQuestionSubmit: PropTypes.func,
};

export default QuestionsAndAnswersForm;
